/**
 * BulkUploadStackOverlay — widget collant bas-droit, style Google Drive.
 *
 * Rendu directement par `<BulkUploadStackProvider>` (colocalisation —
 * l'overlay vit avec son state, pas dans le JSX d'AppShell).
 *
 * Deux états :
 *   - **Compact** (default, ~60px de haut) : barre récap "N uploads · X / Y"
 *     + bouton expand + bouton dismiss-all (uniquement les `done`).
 *   - **Expanded** : 1 ligne par batch avec progress bar, statut, actions
 *     (cancel/dismiss/retry). max-height 60vh, scroll interne.
 *
 * Affiché uniquement si `batches.length > 0` — sinon `null`.
 *
 * Z-index : `--z-sticky` (200) — explicitement en-dessous de `--z-modal`
 * (1000) pour qu'un Modal ouvert (focus-trapped) masque l'overlay. Un
 * upload est une info passive ; un modal demande une décision.
 */
import { useState } from 'react'
import { ChevronDown, ChevronUp, RotateCw, X } from 'lucide-react'

import Button from '@/components/ui/Button'
import type { BulkUploadBatch } from '@/contexts/bulk-upload-stack-context'

import './BulkUploadStackOverlay.css'

interface BulkUploadStackOverlayProps {
  batches: readonly BulkUploadBatch[]
  expanded: boolean
  onExpandedChange: (expanded: boolean) => void
  onCancel: (id: string) => void
  onDismiss: (id: string) => void
  onRetry: (id: string) => string | null
}

export default function BulkUploadStackOverlay({
  batches,
  expanded,
  onExpandedChange,
  onCancel,
  onDismiss,
  onRetry,
}: BulkUploadStackOverlayProps) {
  if (batches.length === 0) return null

  return expanded ? (
    <ExpandedOverlay
      batches={batches}
      onCollapse={() => onExpandedChange(false)}
      onCancel={onCancel}
      onDismiss={onDismiss}
      onRetry={onRetry}
    />
  ) : (
    <CompactOverlay
      batches={batches}
      onExpand={() => onExpandedChange(true)}
      onDismiss={onDismiss}
    />
  )
}

// ──────────────────────────────────────────────────────────────────────────
// Compact — barre collante en bas-droit
// ──────────────────────────────────────────────────────────────────────────

interface CompactProps {
  batches: readonly BulkUploadBatch[]
  onExpand: () => void
  onDismiss: (id: string) => void
}

function CompactOverlay({ batches, onExpand, onDismiss }: CompactProps) {
  const totals = sumTotals(batches)
  const hasInflight = batches.some((b) => b.phase === 'uploading')
  const doneBatches = batches.filter((b) => b.phase === 'done')
  const canDismissAll = doneBatches.length > 0

  // Libellé global : si tout est done, "X imports terminés", sinon en cours.
  const label = hasInflight
    ? `${batches.length} upload${batches.length > 1 ? 's' : ''} · ${totals.completed} / ${totals.total}`
    : `${batches.length} import${batches.length > 1 ? 's' : ''} terminé${batches.length > 1 ? 's' : ''}`

  // Pattern HTML : la racine est un <div> non-interactive (la zone d'expand
  // et le ✕ dismiss-all sont deux <Button> distincts à l'intérieur). Évite
  // l'imbrication button-dans-button (HTML invalide) et respecte
  // `sgraphic/no-raw-html-controls`.
  return (
    <div className="bulk-upload-overlay bulk-upload-overlay-compact" aria-label="Liste des uploads">
      <Button
        variant="quiet"
        size="sm"
        onClick={onExpand}
        className="bulk-upload-overlay-compact-main"
        aria-label="Détailler les uploads en cours"
      >
        <span className="bulk-upload-overlay-icon" aria-hidden="true">
          {hasInflight ? <RotateCw size={16} className="bulk-upload-overlay-spinning" /> : '✓'}
        </span>
        <span className="bulk-upload-overlay-label">{label}</span>
        <ChevronUp size={14} aria-hidden="true" className="bulk-upload-overlay-chevron" />
      </Button>
      {canDismissAll ? (
        <Button
          variant="quiet"
          size="sm"
          onClick={() => {
            for (const b of doneBatches) onDismiss(b.id)
          }}
          aria-label="Fermer les uploads terminés"
          className="bulk-upload-overlay-dismiss"
        >
          <X size={14} aria-hidden="true" />
        </Button>
      ) : null}
    </div>
  )
}

// ──────────────────────────────────────────────────────────────────────────
// Expanded — liste détaillée empilée
// ──────────────────────────────────────────────────────────────────────────

interface ExpandedProps {
  batches: readonly BulkUploadBatch[]
  onCollapse: () => void
  onCancel: (id: string) => void
  onDismiss: (id: string) => void
  onRetry: (id: string) => string | null
}

function ExpandedOverlay({ batches, onCollapse, onCancel, onDismiss, onRetry }: ExpandedProps) {
  return (
    <section
      className="bulk-upload-overlay bulk-upload-overlay-expanded"
      aria-label="Liste des uploads"
    >
      <header className="bulk-upload-overlay-header">
        <span className="bulk-upload-overlay-title">Uploads</span>
        <Button
          variant="quiet"
          size="sm"
          onClick={onCollapse}
          aria-label="Réduire la liste des uploads"
          className="bulk-upload-overlay-collapse"
        >
          <ChevronDown size={16} aria-hidden="true" />
        </Button>
      </header>
      <ul className="bulk-upload-overlay-list">
        {batches.map((batch) => (
          <BatchRow
            key={batch.id}
            batch={batch}
            onCancel={onCancel}
            onDismiss={onDismiss}
            onRetry={onRetry}
          />
        ))}
      </ul>
    </section>
  )
}

// ──────────────────────────────────────────────────────────────────────────
// Une ligne par batch dans l'expanded
// ──────────────────────────────────────────────────────────────────────────

interface BatchRowProps {
  batch: BulkUploadBatch
  onCancel: (id: string) => void
  onDismiss: (id: string) => void
  onRetry: (id: string) => string | null
}

function BatchRow({ batch, onCancel, onDismiss, onRetry }: BatchRowProps) {
  const [showFiles, setShowFiles] = useState(false)
  const completed =
    batch.counts.success +
    batch.counts.duplicate_content +
    batch.counts.duplicate_slug +
    batch.counts.error
  const successTotal = batch.counts.success + batch.counts.duplicate_content
  const actionTotal = batch.counts.error + batch.counts.duplicate_slug
  const percent = batch.total > 0 ? Math.round((completed / batch.total) * 100) : 0

  const statusIcon = (() => {
    if (batch.phase === 'uploading') return '⟳'
    if (batch.cancelled) return '⊘'
    if (actionTotal > 0) return '!'
    return '✓'
  })()

  const statusLabel = (() => {
    if (batch.phase === 'uploading') return `${completed} / ${batch.total}`
    if (batch.cancelled) return `Annulé · ${successTotal} / ${batch.total}`
    if (actionTotal > 0)
      return `${successTotal} ok · ${actionTotal} action${actionTotal > 1 ? 's' : ''}`
    return `${successTotal} / ${batch.total}`
  })()

  const errorRows = batch.progress.filter(
    (p) => p.status === 'error' || p.status === 'duplicate_slug',
  )

  return (
    <li className="bulk-upload-overlay-batch" data-phase={batch.phase}>
      <div className="bulk-upload-overlay-batch-header">
        <span className="bulk-upload-overlay-batch-icon" aria-hidden="true">
          {statusIcon}
        </span>
        <span className="bulk-upload-overlay-batch-label">{batch.label}</span>
        <span className="bulk-upload-overlay-batch-status">{statusLabel}</span>
      </div>
      <div
        className="bulk-upload-overlay-batch-bar"
        role="progressbar"
        aria-valuenow={completed}
        aria-valuemin={0}
        aria-valuemax={batch.total}
      >
        <div className="bulk-upload-overlay-batch-bar-fill" style={{ width: `${percent}%` }} />
      </div>

      {errorRows.length > 0 && batch.phase === 'done' ? (
        <div className="bulk-upload-overlay-batch-errors">
          <Button
            variant="quiet"
            size="sm"
            onClick={() => setShowFiles((v) => !v)}
            aria-expanded={showFiles}
            className="bulk-upload-overlay-batch-toggle"
          >
            {showFiles ? 'Masquer' : 'Voir'} les {errorRows.length} fichier
            {errorRows.length > 1 ? 's' : ''} en échec
          </Button>
          {showFiles ? (
            <ul className="bulk-upload-overlay-batch-error-list">
              {errorRows.map((row, idx) => (
                <li key={`${idx}-${row.filename}`} className="bulk-upload-overlay-batch-error-item">
                  <span className="bulk-upload-overlay-batch-error-name">{row.filename}</span>
                  <span className="bulk-upload-overlay-batch-error-message">{row.message}</span>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}

      <div className="bulk-upload-overlay-batch-actions">
        {batch.phase === 'uploading' ? (
          <Button variant="ghost" size="sm" onClick={() => onCancel(batch.id)}>
            Annuler
          </Button>
        ) : null}
        {batch.phase === 'done' && actionTotal > 0 ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              onRetry(batch.id)
            }}
          >
            Re-tenter {actionTotal} en échec
          </Button>
        ) : null}
        {batch.phase === 'done' ? (
          <Button variant="ghost" size="sm" onClick={() => onDismiss(batch.id)}>
            Fermer
          </Button>
        ) : null}
      </div>
    </li>
  )
}

// ──────────────────────────────────────────────────────────────────────────
// Helpers
// ──────────────────────────────────────────────────────────────────────────

function sumTotals(batches: readonly BulkUploadBatch[]): {
  total: number
  completed: number
} {
  let total = 0
  let completed = 0
  for (const b of batches) {
    total += b.total
    completed +=
      b.counts.success + b.counts.duplicate_content + b.counts.duplicate_slug + b.counts.error
  }
  return { total, completed }
}
